import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Navbar from '../elements/Navbar';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';

const CATEGORIES = ['AI Updaters', 'Image Converter', 'Subscriptions',  'Storefront', 'Taxonomy'];

const EDITOR_AUTHOR = 'mario@getpop.co';

const MenuBar = ({ editor, onImageUpload }) => {
  if (!editor) return null;

  const btnClass = (active) =>
    `px-2 py-1 text-sm rounded border ${active
      ? 'bg-indigo-600 text-white border-indigo-600'
      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`;

  return (
    <div className="flex flex-wrap gap-1 p-2 border-b border-gray-200 bg-gray-50 rounded-t-md">
      <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={btnClass(editor.isActive('bold'))}>B</button>
      <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={btnClass(editor.isActive('italic'))}>I</button>
      <button type="button" onClick={() => editor.chain().focus().toggleStrike().run()} className={btnClass(editor.isActive('strike'))}>S</button>
      <span className="border-l border-gray-300 mx-1" />
      <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} className={btnClass(editor.isActive('heading', { level: 1 }))}>H1</button>
      <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={btnClass(editor.isActive('heading', { level: 2 }))}>H2</button>
      <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={btnClass(editor.isActive('heading', { level: 3 }))}>H3</button>
      <span className="border-l border-gray-300 mx-1" />
      <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={btnClass(editor.isActive('bulletList'))}>• List</button>
      <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={btnClass(editor.isActive('orderedList'))}>1. List</button>
      <button type="button" onClick={() => editor.chain().focus().toggleCodeBlock().run()} className={btnClass(editor.isActive('codeBlock'))}>{'</>'}</button>
      <button type="button" onClick={() => editor.chain().focus().toggleBlockquote().run()} className={btnClass(editor.isActive('blockquote'))}>❝</button>
      <span className="border-l border-gray-300 mx-1" />
      <button type="button" onClick={onImageUpload} className={btnClass(false)}>🖼 Image</button>
    </div>
  );
};

const KnowledgeBaseEditor = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(slug);

  const userEmail = localStorage.getItem('userEmail');
  const isAuthorized = userEmail === EDITOR_AUTHOR;

  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [published, setPublished] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [loadingArticle, setLoadingArticle] = useState(isEditing);

  const imageInputRef = useRef(null);

  const API_BASE = process.env.NODE_ENV === 'development'
    ? process.env.REACT_APP_API_URL_BASE_DEV
    : process.env.REACT_APP_API_URL_BASE_PROD;

  const editor = useEditor({
    extensions: [
      StarterKit,
      Image.configure({ inline: false, allowBase64: false }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'prose prose-gray max-w-none min-h-[400px] p-4 focus:outline-none',
      },
    },
  });

  useEffect(() => {
    if (!isEditing || !editor) return;

    const fetchArticle = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/knowledge_base/articles/${slug}`);
        const article = res.data;
        setTitle(article.title || '');
        setCategory(article.category || '');
        setPublished(article.published);
        editor.commands.setContent(article.body || '');
      } catch (e) {
        setError('Failed to load article.');
      } finally {
        setLoadingArticle(false);
      }
    };

    fetchArticle();
  }, [isEditing, slug, API_BASE, editor]);

  const handleImageUpload = () => {
    imageInputRef.current?.click();
  };

  const handleImageFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API_BASE}/api/knowledge_base/images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const imageUrl = `${API_BASE}${res.data.url}`;
      editor.chain().focus().setImage({ src: imageUrl }).run();
    } catch (e) {
      setError('Image upload failed. Please try again.');
    } finally {
      e.target.value = '';
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      setError('Title is required.');
      return;
    }

    setSaving(true);
    setError('');

    const payload = {
      knowledge_base_article: {
        title,
        body: editor.getHTML(),
        category,
        published,
        author: userEmail,
      },
    };

    try {
      let res;
      if (isEditing) {
        res = await axios.patch(`${API_BASE}/api/knowledge_base/articles/${slug}`, payload);
      } else {
        res = await axios.post(`${API_BASE}/api/knowledge_base/articles`, payload);
      }
      navigate(`/knowledge-base/${res.data.slug}`);
    } catch (e) {
      setError('Failed to save article. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!isAuthorized) {
    return (
      <div>
        <Navbar />
        <div className="container mx-auto px-4 max-w-3xl mt-16 text-center">
          <p className="text-gray-500 text-lg">You don't have permission to create or edit articles.</p>
          <Link to="/knowledge-base" className="text-indigo-600 hover:underline text-sm mt-4 block">
            ← Back to Knowledge Base
          </Link>
        </div>
      </div>
    );
  }

  if (loadingArticle) {
    return (
      <div>
        <Navbar />
        <div className="container mx-auto px-4 max-w-3xl mt-8 text-gray-400 text-sm">Loading...</div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="container mx-auto mb-32 px-4 max-w-3xl">
        <div className="mb-6">
          <Link to="/knowledge-base" className="text-indigo-600 hover:underline text-sm">
            ← Back to Knowledge Base
          </Link>
        </div>

        <h1 className="text-2xl font-semibold text-gray-900 mb-6">
          {isEditing ? 'Edit Article' : 'New Article'}
        </h1>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-4 py-3 mb-4">
            {error}
          </div>
        )}

        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Article title..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={category}
                onChange={e => setCategory(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">No category</option>
                {CATEGORIES.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={published}
                  onChange={e => setPublished(e.target.checked)}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                Published
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
            <div className="border border-gray-300 rounded-md overflow-hidden">
              <MenuBar editor={editor} onImageUpload={handleImageUpload} />
              <EditorContent editor={editor} />
            </div>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImageFileChange}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Link
              to="/knowledge-base"
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </Link>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Publish Article'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeBaseEditor;
